import type { DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { buildTransportAddressFields } from '../modules/orders/transportAddressUtils';
import { resolveOrderDeliveryAddress } from '../utils/orderDeliveryAddress';

export const TRANSPORT_DATE_FORMAT = 'YYYY-MM-DD';
// What the operator sees and types in the pickers; the API keeps ISO above.
export const TRANSPORT_DATE_DISPLAY_FORMAT = 'DD.MM.YYYY';

const EMPTY_DATE_VALUE = null;

export interface TransportFormValues {
  customer?: string;
  currency?: string;
  status?: string;
  loadingAddresses?: string[];
  deliveryAddresses?: string[];
  loadingDate?: Dayjs | null;
  estimatedDelivery?: Dayjs | null;
  actualDelivery?: Dayjs | null;
  transportRate?: number | null;
  totalWeight?: number | null;
  totalVolume?: number | null;
  [key: string]: unknown;
}

// Usability round 2026-10-05: typed dates are parsed in every common form
// (the first one is the display format); „2026-10-12” used to clear the field.
export const TRANSPORT_DATE_INPUT_FORMATS = [TRANSPORT_DATE_DISPLAY_FORMAT, 'YYYY-MM-DD', 'D.M.YYYY', 'DD/MM/YYYY'];

export const transportDatePickerProps: DatePickerProps = {
  format: TRANSPORT_DATE_INPUT_FORMATS,
  needConfirm: false,
  style: { width: '100%' },
};

export function buildTransportFormValues(
  transport: Record<string, any> | undefined,
  order: Record<string, any> | undefined,
): TransportFormValues {
  if (transport) {
    const addressFields = buildTransportAddressFields(transport);

    return {
      ...transport,
      ...addressFields,
      loadingDate: toDayjsValue(transport.loadingDate),
      estimatedDelivery: toDayjsValue(transport.estimatedDelivery),
      actualDelivery: toDayjsValue(transport.actualDelivery),
      transportRate: toNumberValue(transport.transportRate),
      totalWeight: toNumberValue(transport.totalWeight),
      totalVolume: toNumberValue(transport.totalVolume),
    };
  }

  // Unloading place: order „Adres dostawy”, else the customer card address.
  const orderDeliveryAddress = resolveOrderDeliveryAddress(order);
  const addressFields = buildTransportAddressFields({
    loadingAddresses: order?.factoryLocation ? [order.factoryLocation] : [],
    deliveryAddresses: orderDeliveryAddress ? [orderDeliveryAddress] : [],
  });
  const loadingAddresses = addressFields.loadingAddresses.length > 0 ? addressFields.loadingAddresses : [''];
  const deliveryAddresses = addressFields.deliveryAddresses.length > 0 ? addressFields.deliveryAddresses : [''];

  return {
    customer: order?.customer?.name || '',
    // PLN is the internal money base; default an unset order currency to PLN to
    // match the PLN-default rule rather than a legacy 'USD'.
    currency: order?.currency || 'PLN',
    status: 'SCHEDULED',
    ...addressFields,
    loadingAddresses,
    deliveryAddresses,
    loadingDate: EMPTY_DATE_VALUE,
    estimatedDelivery: EMPTY_DATE_VALUE,
    actualDelivery: EMPTY_DATE_VALUE,
    transportRate: null,
    totalWeight: null,
    totalVolume: null,
  };
}

export function buildTransportRequestPayload(values: TransportFormValues): Record<string, unknown> {
  const addressFields = buildTransportAddressFields(values as Record<string, unknown>);

  return {
    ...values,
    ...addressFields,
    loadingDate: toDateString(values.loadingDate),
    estimatedDelivery: toDateString(values.estimatedDelivery),
    actualDelivery: toDateString(values.actualDelivery),
    transportRate: toStringNumber(values.transportRate),
    totalWeight: toStringNumber(values.totalWeight),
    totalVolume: toStringNumber(values.totalVolume),
  };
}

function toDayjsValue(value: unknown): Dayjs | null {
  if (!value) {
    return EMPTY_DATE_VALUE;
  }

  const dateValue = dayjs(value as dayjs.ConfigType);
  return dateValue.isValid() ? dateValue : EMPTY_DATE_VALUE;
}

function toNumberValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function toDateString(value: unknown): string | null {
  if (!dayjs.isDayjs(value)) {
    return null;
  }

  return value.format(TRANSPORT_DATE_FORMAT);
}

function toStringNumber(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return String(value);
}
